// ══════════════════════════════════════════════════════════════════════════════
//  Btn, IconButton y Pressable.
// ══════════════════════════════════════════════════════════════════════════════
import { forwardRef, useRef } from 'react';
import { C, T, R } from '../../design.js';
import { Icon } from './Icon.js';

// Un onClick ASÍNCRONO se bloquea a sí mismo mientras corre: el segundo clic de
// un doble clic nervioso (o de una tablet que repite el toque) llegaba antes de
// que terminara el primero y creaba dos estimaciones, dos pagos o dos reportes.
// No cambia nada visual — la afordancia explícita sigue siendo `loading`—: solo
// ignora los clics que caen dentro de la operación que ya está corriendo. Un
// onClick síncrono (el 90%: abrir un modal, cambiar de pestaña) no se toca.
const useAntiDobleClic = (onClick) => {
  const corriendo = useRef(false);
  return (e) => {
    if (!onClick || corriendo.current) return undefined;
    const r = onClick(e);
    if (r && typeof r.then === 'function') {
      corriendo.current = true;
      const soltar = () => { corriendo.current = false; };
      r.then(soltar, soltar);
    }
    return r;
  };
};

// Btn — jerarquía: variant 'primary' (CTA) | 'secondary' | 'tertiary' (ghost
//   subordinado) + alias retrocompat 'outline'/'ghost'. Prop `danger` tiñe
//   cualquier variante con semántica destructiva. `icon` (nombre) antepone un
//   Icon con gap consistente — corrige los marginRight ad-hoc. `critical` queda
//   DEPRECADO → usar variant="primary" danger (se mantiene por retrocompat).
//   Reenvía el ref al <button> (2026-09-16): un formulario devuelve el foco a su botón
//   cuando el navegador lo soltó mientras el botón esperaba apagado (`loading`).
const Btn = forwardRef(function Btn({ children, onClick, variant="primary", size="md", danger=false, icon, disabled=false, type="button", loading=false, style:sx={}, ...rest }, ref) {
  const isBusy = disabled || loading;   // loading bloquea la interacción
  const handleClick = useAntiDobleClic(onClick);
  const typo = size==="sm" ? { ...T.bodySm, fontWeight:600 } : { ...T.body, fontWeight:600 };
  const base = { ...typo, border:"none", borderRadius:R.ctl, cursor:isBusy?"not-allowed":"pointer", fontFamily:"inherit", letterSpacing:0.1, opacity:disabled?0.5:1,
    ...((icon||loading) ? { display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8 } : {}) };
  const pad = {
    primary:   size==="sm"?"6px 12px":"8px 16px",
    secondary: size==="sm"?"6px 12px":"8px 16px",
    outline:   size==="sm"?"5px 11px":"7px 15px",
    tertiary:  size==="sm"?"5px 10px":"7px 14px",
    ghost:     size==="sm"?"5px 10px":"7px 14px",
    danger:    size==="sm"?"6px 12px":"8px 16px",
    critical:  size==="sm"?"6px 12px":"8px 16px",
  };
  const dest = danger || variant==="danger" || variant==="critical";   // tinte destructivo
  const variants = {
    primary:  { background:dest?C.criticalFill:C.primaryFill, color:C.onPrimary },
    secondary:{ background:C.bg, color:dest?C.critical:C.textMed, border:`1px solid ${dest?C.criticalBorder:C.border}` },
    outline:  { background:"transparent", color:dest?C.critical:C.textMed, border:`1px solid ${dest?C.criticalBorder:C.border}` },
    tertiary: { background:"transparent", color:dest?C.critical:C.textMed },
    ghost:    { background:"transparent", color:dest?C.critical:C.textMed },
    danger:   { background:C.criticalFill, color:C.onPrimary },   // dep → primary danger
    critical: { background:C.criticalFill, color:C.onPrimary },   // dep → primary danger
  };
  const filled = variant==="primary" || variant==="danger" || variant==="critical";
  const hoverCls = filled ? (dest ? " gb-fill-critical" : " gb-fill-primary") : " gb-btn-soft";
  const shine = filled && !isBusy ? " gb-shine" : "";
  return (
    <button ref={ref} type={type} onClick={isBusy?undefined:handleClick} disabled={isBusy} aria-busy={loading||undefined}
      data-variant={variant}
      className={"gb-btn gb-press gb-i gb-focus"+hoverCls+shine}
      style={{...base, padding:pad[variant]||pad.primary, ...(variants[variant]||variants.primary), ...sx}} {...rest}>
      {loading
        ? <span className="gb-spin" style={{width:size==="sm"?13:14, height:size==="sm"?13:14, borderWidth:2, flexShrink:0}} aria-hidden/>
        : (icon && <Icon name={icon} size={size==="sm"?13:14} color="currentColor"/>)}
      {children}
    </button>
  );
});

// IconButton — botón de solo-icono con afordancia (hover/press/focus) integrada.
const IconButton = ({ icon, onClick, title, tone, size=32, disabled=false, active=false, loading=false, style:sx={} }) => {
  const col = tone==="danger"||tone==="critical" ? C.critical : tone==="primary" ? C.primary : tone==="success" ? C.success : C.textMed;
  const isBusy = disabled || loading;
  const iconPx = Math.round(size*0.45);
  const handleClick = useAntiDobleClic(onClick);
  return (
    <button type="button" onClick={isBusy?undefined:handleClick} disabled={isBusy} title={title} aria-label={title} aria-busy={loading||undefined}
      className="gb-boton-icono gb-ghost gb-press gb-i gb-focus"
      style={{width:size,height:size,borderRadius:R.md,border:`1px solid ${active?C.primaryMid:C.border}`,background:active?C.primaryLight:C.surface,display:"flex",alignItems:"center",justifyContent:"center",padding:0,color:active?C.primary:col,flexShrink:0,opacity:disabled?0.5:1,cursor:isBusy?"not-allowed":"pointer",...sx}}>
      {loading
        ? <span className="gb-spin" style={{width:iconPx,height:iconPx,borderWidth:2}} aria-hidden/>
        : (typeof icon==="string" ? <Icon name={icon} size={iconPx} color="currentColor"/> : icon)}
    </button>
  );
};

// Pressable — envuelve cualquier contenido como elemento accionable y accesible
// (teclado + roles). lift=tarjeta que se eleva, row=fila que se ilumina al hover.
const Pressable = ({ children, onClick, lift=false, row=false, disabled=false, className="", style:sx={}, ...rest }) => (
  <div role="button" tabIndex={disabled?-1:0} aria-disabled={disabled||undefined}
    onClick={disabled?undefined:onClick}
    onKeyDown={e=>{ if(!disabled && (e.key==="Enter"||e.key===" ")){ e.preventDefault(); onClick&&onClick(e); } }}
    className={["gb-i","gb-press","gb-focus", lift&&"gb-lift", row&&"gb-row", className].filter(Boolean).join(" ")}
    style={sx} {...rest}>{children}</div>
);


export { Btn, IconButton, Pressable };
